"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import Modal from "./Modal";
import { toast } from "./Toaster";

type ModalKind = "share" | "review" | "cancel";

/** Listing context captured from the share trigger's data-* attributes so
 *  the modal shows the real unit + a working link (it used to be a static
 *  "Marina 4" mockup with a dead .com URL). */
interface ShareCtx {
  slug: string;
  title: string;
  location: string;
  cover: string;
}

let setOpenExternal: ((k: ModalKind | null) => void) | null = null;

export function openModal(kind: ModalKind) {
  setOpenExternal?.(kind);
}

export default function ShareReviewCancelModals() {
  const [open, setOpen] = useState<ModalKind | null>(null);
  const [shareCtx, setShareCtx] = useState<ShareCtx | null>(null);

  useEffect(() => {
    setOpenExternal = setOpen;
    return () => {
      setOpenExternal = null;
    };
  }, []);

  // wire any element with [data-modal="share|review|cancel"]
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      const trigger = t.closest('[data-modal]') as HTMLElement | null;
      if (!trigger) return;
      const kind = trigger.dataset.modal as ModalKind;
      if (kind === "share" || kind === "review" || kind === "cancel") {
        e.preventDefault();
        if (kind === "share") {
          setShareCtx({
            slug: trigger.dataset.shareSlug ?? "",
            title: trigger.dataset.shareTitle ?? "",
            location: trigger.dataset.shareLocation ?? "",
            cover: trigger.dataset.shareCover ?? "",
          });
        }
        setOpen(kind);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const close = () => setOpen(null);

  return (
    <>
      <Modal open={open === "share"} onClose={close}>
        <ShareView close={close} ctx={shareCtx} />
      </Modal>
      <Modal open={open === "review"} onClose={close} panelClass="max-w-[560px]">
        <ReviewView close={close} />
      </Modal>
      <Modal open={open === "cancel"} onClose={close} panelClass="max-w-[520px]">
        <CancelView close={close} />
      </Modal>
    </>
  );
}

/* ============ SHARE ============ */
function ShareView({ close, ctx }: { close: () => void; ctx: ShareCtx | null }) {
  const t = useTranslations("shareReviewCancelModals");
  // Canonical public URL for this listing. Build from the live origin when
  // available (covers www vs apex), else hard-fallback to the production
  // domain so server-rendered / copied links are never the old .com mockup.
  const origin =
    typeof window !== "undefined" && window.location.origin
      ? window.location.origin
      : "https://bluekeys.co";
  const url = ctx?.slug ? `${origin}/listings/${ctx.slug}` : origin;
  const title = ctx?.title || "this home";
  const waText = encodeURIComponent(`Check out ${title} on BlueKeys — ${url}`);

  const copyLink = () => {
    navigator.clipboard?.writeText(url);
    toast(t("linkCopied"), "success");
    close();
  };

  return (
    <>
      <h2 className="h-display text-2xl font-semibold mb-5">{t("shareTitle")}</h2>
      <div className="flex gap-3 p-3.5 bg-ss-bg-soft rounded-[14px] mb-5">
        <div
          className="w-15 h-15 rounded-lg bg-ss-line bg-cover bg-center flex-none"
          style={{ width: 60, height: 60, backgroundImage: ctx?.cover ? `url(${ctx.cover})` : undefined }}
        />
        <div className="min-w-0">
          <strong className="block text-sm truncate">{title}</strong>
          {ctx?.location && (
            <div className="text-[12.5px] text-ss-muted truncate">{ctx.location}</div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2.5 mb-4">
        <ShareBtn icon="🔗" label={t("copyLink")} onClick={copyLink} />
        <ShareBtn
          icon="●"
          label={t("whatsapp")}
          color="#25d366"
          href={`https://wa.me/?text=${waText}`}
        />
        <ShareBtn
          icon="✉"
          label={t("email")}
          href={`mailto:?subject=${encodeURIComponent(title + " · BlueKeys")}&body=${waText}`}
        />
      </div>
      <div className="flex gap-2 bg-ss-bg-soft p-2.5 rounded-lg items-center text-[13px] font-mono text-ss-muted overflow-hidden">
        <span className="truncate">{url}</span>
      </div>
    </>
  );
}

function ShareBtn({ icon, label, color, onClick, href }: { icon: string; label: string; color?: string; onClick?: () => void; href?: string }) {
  const cls = "bg-white border border-ss-line p-2.5 h-20 rounded-[10px] flex flex-col items-center justify-center gap-1.5 hover:border-ss-text";
  const inner = (
    <>
      <span className="text-[22px]" style={color ? { color } : undefined}>{icon}</span>
      <span className="text-[11px] font-semibold">{label}</span>
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={cls}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

/* ============ REVIEW ============ */
function ReviewView({ close }: { close: () => void }) {
  const t = useTranslations("shareReviewCancelModals");
  const [overall, setOverall] = useState(0);
  const [cleanliness, setCleanliness] = useState(0);
  const [communication, setCommunication] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [value, setValue] = useState(0);

  function submit() {
    if (!overall) {
      toast(t("reviewToastMissing"), "danger");
      return;
    }
    toast(t("reviewToastSuccess", { rating: overall }), "success");
    close();
  }

  return (
    <>
      <div className="flex items-center gap-3.5 pb-4 border-b border-ss-line mb-5">
        <div className="w-16 h-16 rounded-[10px] bg-cover bg-center flex-none"
          style={{ backgroundImage: "url(https://admin-assets-prod1.s3.amazonaws.com/unitTypes/5660/images/IMG_7206.1775150497220.JPG)" }}
        />
        <div>
          <strong className="text-[15px] block">3BR Sea View | Marina Views</strong>
          <div className="text-[13px] text-ss-muted">Marassi · Jul 12-15 · Karim H.</div>
        </div>
      </div>
      <h2 className="h-display text-xl font-semibold mb-1">{t("reviewTitle")}</h2>
      <p className="text-[13.5px] text-ss-muted mb-5">{t("reviewSub")}</p>

      <div className="mb-4">
        <label className="text-[11.5px] font-bold uppercase tracking-wider text-ss-muted block mb-1.5">
          {t("overallRating")}
        </label>
        <Stars value={overall} onChange={setOverall} size={32} starAriaLabel={t("starAriaLabel", { n: "{n}" })} />
      </div>

      <div className="grid grid-cols-2 gap-3.5 mb-5">
        <SubRating label={t("cleanliness")} value={cleanliness} onChange={setCleanliness} starAriaLabel={t("starAriaLabel", { n: "{n}" })} />
        <SubRating label={t("communication")} value={communication} onChange={setCommunication} starAriaLabel={t("starAriaLabel", { n: "{n}" })} />
        <SubRating label={t("accuracy")} value={accuracy} onChange={setAccuracy} starAriaLabel={t("starAriaLabel", { n: "{n}" })} />
        <SubRating label={t("value")} value={value} onChange={setValue} starAriaLabel={t("starAriaLabel", { n: "{n}" })} />
      </div>

      <div className="mb-4">
        <label className="text-[11.5px] font-bold uppercase tracking-wider text-ss-muted block mb-1.5">
          {t("tellGuests")}
        </label>
        <textarea
          rows={4}
          placeholder={t("reviewPlaceholder")}
          className="w-full p-3 border border-ss-line rounded-[10px] outline-none focus:border-ss-blue text-sm"
        />
      </div>

      <button
        type="button"
        onClick={submit}
        className="w-full bg-ss-blue text-white font-semibold py-3.5 rounded-full hover:bg-ss-blue-dark"
      >
        {t("submitReview")}
      </button>
      <p className="text-center text-[12px] text-ss-muted mt-3.5">
        {t("reviewPublicNote")}
      </p>
    </>
  );
}

function SubRating({ label, value, onChange, starAriaLabel }: { label: string; value: number; onChange: (v: number) => void; starAriaLabel: string }) {
  return (
    <div>
      <label className="text-[11.5px] font-bold uppercase tracking-wider text-ss-muted block mb-1.5">
        {label}
      </label>
      <Stars value={value} onChange={onChange} size={18} starAriaLabel={starAriaLabel} />
    </div>
  );
}

function Stars({ value, onChange, size, starAriaLabel }: { value: number; onChange: (v: number) => void; size: number; starAriaLabel: string }) {
  const [hover, setHover] = useState(0);
  const display = hover || value;
  return (
    <div className="flex gap-1.5" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onMouseEnter={() => setHover(s)}
          onClick={() => onChange(s)}
          aria-label={starAriaLabel.replace("{n}", String(s))}
          style={{
            color: s <= display ? "#fbbf24" : "#e6ebf2",
            fontSize: size,
            cursor: "pointer",
            background: "transparent",
            border: 0,
            padding: 0,
            lineHeight: 1,
          }}
        >
          ★
        </button>
      ))}
    </div>
  );
}

/* ============ CANCEL ============ */
function CancelView({ close }: { close: () => void }) {
  const t = useTranslations("shareReviewCancelModals");
  function confirm() {
    toast(t("cancelToast", { amount: "28,580" }), "success");
    close();
  }
  return (
    <>
      <h2 className="h-display text-2xl font-semibold mb-3.5">{t("cancelTitle")}</h2>

      <div className="bg-[#e8f7ee] border border-[#b6e2c4] text-[#0f7d3a] p-3.5 rounded-[10px] mb-4 flex gap-2.5">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-none mt-0.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
          <circle cx="12" cy="12" r="9" />
        </svg>
        <div>
          <strong className="block mb-0.5">{t("refundBannerTitle")}</strong>
          <span className="text-[13px]">
            {t("refundBannerBody", { days: "56", deadline: "Jul 1, 3:00 PM" })}
          </span>
        </div>
      </div>

      <div className="bg-ss-bg-soft p-4 rounded-[10px] mb-5">
        <div className="text-[11.5px] font-bold uppercase tracking-wider text-ss-muted mb-2.5">
          {t("refundSchedule")}
        </div>
        <RefundRow
          when={t("refundToday", { deadline: "Jul 1, 3:00 PM" })}
          sub={t("refundTodaySub")}
          amt={t("refundTodayAmt", { amount: "28,580" })}
          color="text-[#0f7d3a]"
        />
        <RefundRow
          when={t("refundPartial", { from: "Jul 1", to: "Jul 4" })}
          sub={t("refundPartialSub")}
          amt={t("refundPartialAmt", { amount: "14,290" })}
        />
        <RefundRow
          when={t("refundNone")}
          sub={t("refundNoneSub")}
          amt={t("refundNoneAmt")}
          color="text-[#c43b3b]"
          last
        />
      </div>

      <div className="mb-3">
        <label className="text-[11.5px] font-bold uppercase tracking-wider text-ss-muted block mb-1.5">
          {t("cancellationReason")}
        </label>
        <select className="w-full p-3 border border-ss-line rounded-[10px] outline-none focus:border-ss-blue text-sm">
          <option>{t("reasonPlansChanged")}</option>
          <option>{t("reasonBetterDates")}</option>
          <option>{t("reasonTooExpensive")}</option>
          <option>{t("reasonMistake")}</option>
          <option>{t("reasonOther")}</option>
        </select>
      </div>

      <div className="bg-ss-blue-50 p-3.5 rounded-[10px] mb-5 text-[13px]">
        <strong className="block mb-1">{t("changeDatesTitle")}</strong>
        <span className="text-ss-muted">
          {t("changeDatesSub")}
        </span>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={close}
          className="flex-1 bg-white border border-ss-line font-semibold py-3 rounded-full"
        >
          {t("keepBooking")}
        </button>
        <button
          type="button"
          onClick={confirm}
          className="flex-1 bg-[#c43b3b] text-white font-semibold py-3 rounded-full hover:bg-[#a63333]"
        >
          {t("cancelConfirm", { amount: "28,580" })}
        </button>
      </div>
    </>
  );
}

function RefundRow({
  when,
  sub,
  amt,
  color,
  last,
}: {
  when: string;
  sub: string;
  amt: string;
  color?: string;
  last?: boolean;
}) {
  return (
    <div className={`flex justify-between items-start py-2.5 ${last ? "" : "border-b border-ss-line"} text-[13.5px]`}>
      <div>
        <strong>{when}</strong>
        <div className="text-[12px] text-ss-muted">{sub}</div>
      </div>
      <strong className={color || ""}>{amt}</strong>
    </div>
  );
}
